import {
  TRANSFORMATION_STATUS_LABELS,
  type StockTransformation,
} from "@artisancode/api-types";
import {
  CheckCircle2,
  Coffee,
  Eye,
  MoreHorizontal,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateWIB } from "@/lib/date-utils";
import { formatCurrency, formatCurrencyRounded } from "@/lib/utils";

import { summarizeProduction } from "./production-utils";

export interface ProductionRowsProps {
  rows: StockTransformation[];
  canComplete: boolean;
  isCompleting?: boolean;
  onViewDetail: (trans: StockTransformation) => void;
  onComplete: (id: string) => void;
  onCancelRequest: (trans: StockTransformation) => void;
}

export function ProductionDesktopTable({
  rows,
  canComplete,
  isCompleting,
  onViewDetail,
  onComplete,
  onCancelRequest,
}: ProductionRowsProps) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40">
            <TableHead>Kode & Tanggal</TableHead>
            <TableHead>Menu</TableHead>
            <TableHead className="text-right">Output Jadi</TableHead>
            <TableHead className="text-right">HPP Satuan</TableHead>
            <TableHead className="text-right">Total Biaya</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead className="w-[80px] text-right">Aksi</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={7}
                className="h-32 text-center text-sm text-muted-foreground"
              >
                Tidak ada batch produksi yang sesuai dengan filter.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((item) => {
              const statusMeta = TRANSFORMATION_STATUS_LABELS[item.status];
              const { menus, totalInputCost, menuNames } =
                summarizeProduction(item);
              const uomName = menus[0]?.outputLine.uomName ?? "";

              return (
                <TableRow key={item.id} className="hover:bg-muted/30">
                  <TableCell>
                    <div className="font-mono text-sm font-semibold">
                      {item.code}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDateWIB(item.date)}
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <Coffee className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>{menuNames || "Minuman"}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {menus.length} menu
                    </div>
                  </TableCell>

                  <TableCell className="text-right font-mono font-bold text-primary">
                    {menus.length > 0 ? (
                      <>
                        {item.totalOutputQty.toLocaleString("id-ID")}{" "}
                        <span className="text-xs font-normal text-muted-foreground">
                          {uomName}
                        </span>
                      </>
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  <TableCell className="text-right font-mono text-sm">
                    {menus.length === 1 && menus[0]
                      ? formatCurrency(menus[0].unitCost)
                      : "Per menu"}
                  </TableCell>

                  <TableCell className="text-right font-mono text-sm font-semibold">
                    {formatCurrencyRounded(totalInputCost)}
                  </TableCell>

                  <TableCell className="text-center">
                    <Badge
                      variant={statusMeta.variant}
                      className="text-[11px] font-medium"
                    >
                      {statusMeta.label}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => onViewDetail(item)}>
                          <Eye className="mr-2 h-4 w-4" />
                          Lihat Rincian
                        </DropdownMenuItem>

                        {item.status === "draft" && canComplete && (
                          <DropdownMenuItem
                            onClick={() => onComplete(item.id)}
                            disabled={isCompleting}
                            className="text-emerald-600 focus:text-emerald-600"
                          >
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            Selesaikan Batch
                          </DropdownMenuItem>
                        )}

                        {item.status !== "cancelled" && (
                          <DropdownMenuItem
                            onClick={() => onCancelRequest(item)}
                            className="text-destructive focus:text-destructive"
                          >
                            <XCircle className="mr-2 h-4 w-4" />
                            Batalkan Batch
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
